import { coco } from '@cotera/api';
import { DG } from '@cotera/sdk/core';
import type {
  ChatSeedBuildInput,
  ChatSeedPreSubmitContext,
  ChatSeedPreSubmitResult,
  ChatSeedSubmitParams,
} from '../../chat-seed/types';
import { seedArtifactIsNew } from '../../chat-seed/types';
import { getAgentSeedRuntime } from './runtime';

const AGENT_ARTIFACT_TYPE = 'agent';

/**
 * Seeded agent id. The seed deliberately does not send `agentId` on the chat
 * context (that would link the chat *as* the agent); the agent is identified by
 * its seeded artifact and the `focus_agent` action.
 */
function seededAgentId(params: ChatSeedSubmitParams): string | undefined {
  return params.initialArtifacts?.find(
    (entry) =>
      entry.artifactType === AGENT_ARTIFACT_TYPE &&
      entry.artifactId !== undefined
  )?.artifactId;
}

function resolveAgentSeedInputFromSubmit(
  params: ChatSeedSubmitParams
): ChatSeedBuildInput | null {
  const agentId = seededAgentId(params);
  if (agentId === undefined) {
    return null;
  }

  const artifact = params.initialArtifacts?.find(
    (entry) =>
      entry.artifactType === AGENT_ARTIFACT_TYPE && entry.artifactId === agentId
  );
  if (artifact === undefined) {
    return null;
  }

  const data = artifact.data as
    | { isNew?: boolean; agentKey?: string }
    | null
    | undefined;

  return {
    artifactId: agentId,
    isNew: data?.isNew === true,
    ...(data?.agentKey !== undefined && { agentKey: data.agentKey }),
  };
}

function readPersistSnapshot(agentId: string): {
  editorState: DG.DocumentEditor.DocumentState;
  model: string;
} {
  const runtime = getAgentSeedRuntime(agentId);
  if (runtime !== undefined) {
    return runtime.getPersistSnapshot();
  }

  return {
    editorState: { type: 'doc', content: [] },
    model: coco.DEFAULT_AGENT_MODEL,
  };
}

async function persistNewAgent(
  ctx: ChatSeedPreSubmitContext,
  input: ChatSeedBuildInput
): Promise<ChatSeedPreSubmitResult> {
  const { editorState, model } = readPersistSnapshot(input.artifactId);

  const createResult = await ctx.clients.agent.create({
    id: input.artifactId,
    editorState,
    model,
    ...(ctx.folderId !== undefined && { folderId: ctx.folderId }),
  });

  if (createResult.isErr()) {
    const message =
      createResult.error.t === 'application'
        ? createResult.error.err.message ?? 'Failed to create agent'
        : 'Failed to create agent';
    return { ok: false, message };
  }

  getAgentSeedRuntime(input.artifactId)?.markPersisted();

  return { ok: true };
}

export async function agentChatSeedPreSubmit(
  ctx: ChatSeedPreSubmitContext
): Promise<ChatSeedPreSubmitResult | null> {
  const agentId = seededAgentId(ctx.params);
  if (
    agentId === undefined ||
    !seedArtifactIsNew(ctx.params, AGENT_ARTIFACT_TYPE, agentId)
  ) {
    return null;
  }

  const input = resolveAgentSeedInputFromSubmit(ctx.params);
  if (input === null) {
    return null;
  }

  return persistNewAgent(ctx, input);
}
